import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KILO } from '../../constants/KILO_THEME'
import { clientLocalDate } from '../../lib/clientContext'
import type { AppliedEvidenceRule } from '../../lib/dailyOverrideTypes'
import {
  countNewEvidenceReasons,
  markEvidenceReasonsSeen,
} from '../../lib/evidenceReadState'
import { consumerEvidenceLabel, sanitizeEvidenceAction } from '../../lib/evidenceConsumerLabels'

type DecisionEvidencePanelProps = {
  evidence: AppliedEvidenceRule[]
  /** @deprecated Card shell removed; kept for call-site compatibility */
  embedded?: boolean
  defaultExpanded?: boolean
  /** When true, bold heading + badge for unseen Kilo reasons (per calendar day). */
  trackUpdates?: boolean
  onSeenChange?: () => void
}

const DecisionEvidencePanel = ({
  evidence,
  defaultExpanded = false,
  trackUpdates = false,
  onSeenChange,
}: DecisionEvidencePanelProps) => {
  const { t } = useTranslation()
  const [panelOpen, setPanelOpen] = useState(defaultExpanded)
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [seenVersion, setSeenVersion] = useState(0)

  const dateId = clientLocalDate()

  const sorted = useMemo(
    () => [...evidence].sort((a, b) => a.rule_key.localeCompare(b.rule_key)),
    [evidence],
  )

  const newReasonCount = useMemo(() => {
    void seenVersion
    if (!trackUpdates) return 0
    return countNewEvidenceReasons(dateId, sorted)
  }, [trackUpdates, dateId, sorted, seenVersion])

  if (sorted.length === 0) return null

  const hasNewReasons = newReasonCount > 0

  const togglePanel = () => {
    setPanelOpen((open) => {
      const next = !open
      if (next && trackUpdates) {
        markEvidenceReasonsSeen(dateId, sorted)
        setSeenVersion((v) => v + 1)
        onSeenChange?.()
      }
      return next
    })
  }

  return (
    <div className={KILO.embeddedSection}>
      <button
        type="button"
        onClick={togglePanel}
        className="mt-3 flex w-full items-center gap-2 text-left"
        aria-expanded={panelOpen}
      >
        <span
          className={`text-xs font-bold uppercase tracking-widest ${
            hasNewReasons ? KILO.text.primary : 'text-neutral-500 dark:text-neutral-500'
          }`}
        >
          {t('coach.evidence.title')}
        </span>
        {hasNewReasons ? (
          <span
            className="flex h-5 min-w-5 items-center justify-center rounded-full bg-kilo-accent px-1.5 font-mono text-[10px] font-bold text-black"
            aria-label={t('coach.evidence.newReasons', { count: newReasonCount })}
          >
            {newReasonCount > 9 ? '9+' : newReasonCount}
          </span>
        ) : null}
        <span className="ml-auto text-[10px] text-neutral-500 dark:text-neutral-400">{panelOpen ? '▲' : '▼'}</span>
      </button>

      {!panelOpen ? (
        <p className="sr-only">{t('coach.evidence.subtitle')}</p>
      ) : (
        <>
          <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-500 leading-relaxed">
            {t('coach.evidence.subtitle')}
          </p>
          <ul className="mt-3 divide-y divide-kilo-line/50">
            {sorted.map((rule) => {
              const open = expandedKey === rule.rule_key
              const consumerLabel = consumerEvidenceLabel(rule.rule_key, rule.action_summary)
              const consumerAction = sanitizeEvidenceAction(rule.action_summary)
              return (
                <li key={rule.rule_key} className="py-3 first:pt-0 last:pb-0">
                  <button
                    type="button"
                    className="flex w-full items-start justify-between gap-3 text-left"
                    onClick={() => setExpandedKey(open ? null : rule.rule_key)}
                    aria-expanded={open}
                  >
                    <span className="min-w-0">
                      <span className={`block text-sm font-semibold ${KILO.text.primary}`}>
                        {consumerLabel}
                      </span>
                      <span className={`mt-0.5 block text-xs leading-relaxed text-neutral-500 dark:text-neutral-400`}>
                        {consumerAction}
                      </span>
                    </span>
                    <span
                      className="mt-0.5 shrink-0 font-mono text-xs text-neutral-500 dark:text-neutral-400"
                      aria-hidden
                    >
                      {open ? '−' : '+'}
                    </span>
                  </button>

                  {open ? (
                    <div className="mt-2.5 border-t border-kilo-line/30 pt-2.5">
                      <p className={`text-sm leading-relaxed text-neutral-500 dark:text-neutral-400`}>
                        {rule.physiological_mechanism}
                      </p>

                      {rule.personal_signals.length > 0 ? (
                        <div className="mt-2">
                          <p className={KILO.sectionLabel}>{t('coach.evidence.yourSignals')}</p>
                          <ul className="mt-1 list-inside list-disc text-xs text-neutral-500 dark:text-neutral-400">
                            {rule.personal_signals.map((signal) => (
                              <li key={signal}>{signal}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}

                      {rule.evidence_refs.length > 0 ? (
                        <div className="mt-3">
                          <p className={KILO.sectionLabel}>{t('coach.evidence.sources')}</p>
                          <ul className="mt-1 space-y-1">
                            {rule.evidence_refs.map((ref) => (
                              <li key={ref.source_id} className="text-xs text-neutral-500 dark:text-neutral-400">
                                {ref.url ? (
                                  <a
                                    href={ref.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-kilo-accent underline-offset-2 hover:underline"
                                  >
                                    {ref.short_label}
                                  </a>
                                ) : (
                                  ref.short_label
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </>
      )}
    </div>
  )
}

export default DecisionEvidencePanel
